(() => {
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];

  const lockBody = (locked) => document.documentElement.classList.toggle('overflow-hidden', locked);

  // Homepage Gumlet hero. Keep the poster as the initial/LCP visual and only
  // attach the third-party player after the page has finished its first load.
  // This avoids making the video player part of the critical rendering path.
  const gumletHeroFrames = qsa('[data-gumlet-hero-frame]');
  if (gumletHeroFrames.length && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    const activateGumletFrame = (frame) => {
      if (frame.dataset.gumletActivated === 'true') return;
      const src = frame.dataset.src;
      if (!src) return;
      frame.dataset.gumletActivated = 'true';
      frame.addEventListener('load', () => frame.classList.add('is-loaded'), { once: true });
      frame.src = src;
    };

    const activateGumletHero = () => {
      const start = () => gumletHeroFrames.forEach(activateGumletFrame);
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(start, { timeout: 900 });
      } else {
        window.setTimeout(start, 250);
      }
    };

    if (document.readyState === 'complete') activateGumletHero();
    else window.addEventListener('load', activateGumletHero, { once: true });
  }

  // Premium loader with visible progress.
  const loader = qs('#invita-loader');
  const loaderBar = qs('#invita-loader-bar');
  const loaderPercent = qs('#invita-loader-percent');
  if (loader && loaderBar && loaderPercent) {
    lockBody(true);
    const seenBefore = sessionStorage.getItem('invita-seen') === '1';
    let progress = seenBefore ? 58 : 8;
    const paint = () => {
      loaderBar.style.transform = `scaleX(${progress / 100})`;
      loaderPercent.textContent = `${Math.round(progress)}%`;
    };
    paint();
    const timer = window.setInterval(() => {
      progress = Math.min(91, progress + Math.max(1, (93 - progress) * 0.08));
      paint();
    }, 90);
    window.addEventListener('load', () => {
      window.clearInterval(timer);
      progress = 100;
      paint();
      const finishDelay = seenBefore ? 160 : 320;
      sessionStorage.setItem('invita-seen', '1');
      window.setTimeout(() => {
        loader.classList.add('opacity-0', 'pointer-events-none');
        lockBody(false);
        window.setTimeout(() => loader.remove(), 750);
      }, finishDelay);
    }, { once: true });
  }

  // Stable one-time reveal transitions. These never drive layout from scroll position,
  // so cards cannot collide or jump while the user reverses the scroll direction.
  const revealEls = qsa('[data-reveal]');
  revealEls.forEach((el) => el.classList.add('invita-reveal-ready'));
  const revealObserver = new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      observer.unobserve(entry.target);
    });
  }, { threshold: 0.08, rootMargin: '12% 0px -5%' });
  revealEls.forEach((el) => revealObserver.observe(el));

  // Header depth while scrolling.
  const header = qs('#site-header');
  const syncHeader = () => {
    if (!header) return;
    header.classList.toggle('shadow-[0_18px_70px_rgba(23,19,15,0.18)]', window.scrollY > 20);
  };
  syncHeader();
  window.addEventListener('scroll', syncHeader, { passive: true });

  // Mobile navigation. Submenus stay collapsed by default and open only
  // when their dedicated chevron is pressed. This keeps the drawer compact on
  // phones while still letting the parent label remain a normal navigable link.
  const mobileMenu = qs('#mobile-menu');
  const mobileOverlay = qs('#mobile-menu-overlay');
  const mobileOpenButtons = qsa('[data-mobile-open]');
  const mobileSubmenus = qsa('[data-mobile-submenu]');

  const setMobileSubmenu = (item, open) => {
    const button = qs('[data-mobile-submenu-toggle]', item);
    const panel = qs('[data-mobile-submenu-panel]', item);
    const icon = qs('[data-mobile-submenu-icon]', item);
    if (!button || !panel) return;
    button.setAttribute('aria-expanded', String(open));
    panel.setAttribute('aria-hidden', String(!open));
    panel.toggleAttribute('inert', !open);
    panel.classList.toggle('opacity-0', !open);
    panel.classList.toggle('opacity-100', open);
    panel.style.maxHeight = open ? `${panel.scrollHeight}px` : '0px';
    if (icon) icon.style.transform = open ? 'rotate(180deg)' : 'rotate(0deg)';
  };

  const closeMobileSubmenus = (except = null) => {
    mobileSubmenus.forEach((item) => {
      if (item !== except) setMobileSubmenu(item, false);
    });
  };

  mobileSubmenus.forEach((item) => {
    const button = qs('[data-mobile-submenu-toggle]', item);
    if (!button) return;
    button.addEventListener('click', () => {
      const opening = button.getAttribute('aria-expanded') !== 'true';
      closeMobileSubmenus(opening ? item : null);
      setMobileSubmenu(item, opening);
    });
  });

  const setMobile = (open) => {
    if (!mobileMenu || !mobileOverlay) return;
    mobileMenu.classList.toggle('-translate-x-full', !open);
    mobileMenu.classList.toggle('translate-x-0', open);
    mobileMenu.setAttribute('aria-hidden', String(!open));
    mobileMenu.toggleAttribute('inert', !open);
    mobileOverlay.classList.toggle('opacity-0', !open);
    mobileOverlay.classList.toggle('opacity-100', open);
    mobileOverlay.classList.toggle('pointer-events-none', !open);
    mobileOverlay.classList.toggle('pointer-events-auto', open);
    mobileOpenButtons.forEach((button) => button.setAttribute('aria-expanded', String(open)));
    if (!open) closeMobileSubmenus();
    lockBody(open);
  };
  mobileOpenButtons.forEach((btn) => btn.addEventListener('click', () => setMobile(true)));
  qsa('[data-mobile-close]').forEach((btn) => btn.addEventListener('click', () => setMobile(false)));

  window.addEventListener('resize', () => {
    mobileSubmenus.forEach((item) => {
      const button = qs('[data-mobile-submenu-toggle]', item);
      const panel = qs('[data-mobile-submenu-panel]', item);
      if (button?.getAttribute('aria-expanded') === 'true' && panel) panel.style.maxHeight = `${panel.scrollHeight}px`;
    });
    if (window.innerWidth >= 1024 && mobileMenu?.getAttribute('aria-hidden') === 'false') setMobile(false);
  }, { passive: true });

  // Animated in-place search. The header button never sends visitors to a separate search page.
  const searchOverlay = qs('#invita-search-overlay');
  const searchPanel = qs('#invita-search-panel');
  const searchInput = qs('[data-search-input]');
  const searchForm = qs('[data-search-form]');
  const searchResults = qs('[data-search-results]');
  const searchStatus = qs('[data-search-status]');
  const searchEmpty = qs('[data-search-empty]');
  let searchTimer = null;
  let searchRequest = 0;

  const setSearch = (open) => {
    if (!searchOverlay || !searchPanel) return;
    if (open) setMobile(false);
    searchOverlay.classList.toggle('opacity-0', !open);
    searchOverlay.classList.toggle('opacity-100', open);
    searchOverlay.classList.toggle('pointer-events-none', !open);
    searchOverlay.classList.toggle('pointer-events-auto', open);
    searchPanel.classList.toggle('translate-y-8', !open);
    searchPanel.classList.toggle('translate-y-0', open);
    searchPanel.classList.toggle('scale-[0.985]', !open);
    searchPanel.classList.toggle('scale-100', open);
    searchOverlay.setAttribute('aria-hidden', String(!open));
    lockBody(open);
    if (open) window.setTimeout(() => searchInput?.focus({ preventScroll: true }), 180);
  };

  const renderSearchResults = (data, query) => {
    if (!searchResults || !searchStatus || !searchEmpty) return;
    const resources = data?.resources?.results || {};
    const products = Array.isArray(resources.products) ? resources.products : [];
    const pages = Array.isArray(resources.pages) ? resources.pages : [];
    const articles = Array.isArray(resources.articles) ? resources.articles : [];
    const combined = [
      ...products.map((item) => ({ ...item, type: 'Product' })),
      ...pages.map((item) => ({ ...item, type: 'Page' })),
      ...articles.map((item) => ({ ...item, type: 'Journal' }))
    ].slice(0, 8);

    searchStatus.textContent = combined.length ? `${combined.length} result${combined.length === 1 ? '' : 's'} for “${query}”` : `No results for “${query}”`;
    searchEmpty.textContent = 'No matches yet. Try a treatment name, product, location or topic.';
    searchEmpty.classList.toggle('hidden', combined.length > 0);
    searchResults.innerHTML = combined.map((item, index) => {
      const imageUrl = item.image || item.featured_image?.url || item.featured_image || '';
      const image = imageUrl
        ? `<span class="h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-sand sm:h-20 sm:w-20"><img src="${escapeHtml(imageUrl)}" alt="" class="h-full w-full object-cover" loading="lazy"></span>`
        : `<span class="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-sand text-[9px] font-bold uppercase tracking-[0.18em] text-bronze sm:h-20 sm:w-20">Invita</span>`;
      return `<a href="${escapeHtml(item.url || '#')}" class="invita-search-result group flex items-center gap-4 rounded-[1.35rem] border border-ink/8 bg-white/55 p-3 transition duration-300 hover:-translate-y-0.5 hover:border-bronze/35 hover:bg-white" style="--search-index:${index}">
        ${image}
        <span class="min-w-0 flex-1"><span class="block text-[8px] font-bold uppercase tracking-[0.2em] text-bronze">${escapeHtml(item.type)}</span><span class="mt-1 block truncate font-display text-2xl leading-none sm:text-3xl">${escapeHtml(item.title || '')}</span>${item.price ? `<span class="mt-2 block text-xs font-semibold text-ink/55">${escapeHtml(item.price)}</span>` : ''}</span>
        <span class="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-ink/10 transition group-hover:bg-ink group-hover:text-white">→</span>
      </a>`;
    }).join('');
  };

  const runPredictiveSearch = async (query) => {
    const clean = String(query || '').trim();
    if (!searchResults || !searchStatus || !searchEmpty) return;
    if (clean.length < 2) {
      searchStatus.textContent = 'Start typing to explore Invita';
      searchResults.innerHTML = '';
      searchEmpty.classList.add('hidden');
      return;
    }
    const requestId = ++searchRequest;
    searchStatus.textContent = 'Searching Invita…';
    searchEmpty.classList.add('hidden');
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(clean)}&resources[type]=product,page,article&resources[limit]=8&resources[options][unavailable_products]=last`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Search request failed');
      const data = await response.json();
      if (requestId === searchRequest) renderSearchResults(data, clean);
    } catch (_) {
      if (requestId !== searchRequest) return;
      searchStatus.textContent = 'Search is temporarily unavailable';
      searchResults.innerHTML = '';
      searchEmpty.textContent = 'Please try again in a moment.';
      searchEmpty.classList.remove('hidden');
    }
  };

  qsa('[data-search-open]').forEach((button) => button.addEventListener('click', () => setSearch(true)));
  qsa('[data-search-close]').forEach((button) => button.addEventListener('click', () => setSearch(false)));
  searchInput?.addEventListener('input', () => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => runPredictiveSearch(searchInput.value), 145);
  });
  searchForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    window.clearTimeout(searchTimer);
    runPredictiveSearch(searchInput?.value || '');
  });

  // Accordions.
  qsa('[data-accordion]').forEach((item) => {
    const button = qs('[data-accordion-button]', item);
    const panel = qs('[data-accordion-panel]', item);
    if (!button || !panel) return;
    button.addEventListener('click', () => {
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      panel.style.maxHeight = open ? '0px' : `${panel.scrollHeight}px`;
      const icon = qs('[data-accordion-icon]', item);
      if (icon) icon.style.transform = open ? 'rotate(0deg)' : 'rotate(45deg)';
    });
  });

  // Quantity controls.
  qsa('[data-qty-plus]').forEach((btn) => btn.addEventListener('click', () => {
    const input = qs('[data-qty]', btn.closest('form'));
    if (input) input.value = Math.max(1, Number(input.value || 1) + 1);
  }));
  qsa('[data-qty-minus]').forEach((btn) => btn.addEventListener('click', () => {
    const input = qs('[data-qty]', btn.closest('form'));
    if (input) input.value = Math.max(1, Number(input.value || 1) - 1);
  }));

  // Variant price updates.
  qsa('[data-product-form]').forEach((form) => {
    const select = qs('[data-variant-select]', form);
    const json = qs('[data-product-json]', form.parentElement);
    const priceEl = qs('[data-product-price]', form.parentElement);
    const submit = qs('[type="submit"]', form);
    if (!select || !json) return;
    let variants = [];
    try { variants = JSON.parse(json.textContent); } catch (_) { variants = []; }
    select.addEventListener('change', () => {
      const variant = variants.find((v) => String(v.id) === select.value);
      if (!variant) return;
      if (priceEl) {
        const currency = window.Shopify?.currency?.active || 'USD';
        priceEl.textContent = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(variant.price / 100);
      }
      if (submit) submit.disabled = !variant.available;
    });
  });

  // AJAX cart drawer.
  const drawer = qs('#cart-drawer');
  const overlay = qs('#cart-overlay');
  const drawerContent = qs('#cart-drawer-content');
  const drawerFooter = qs('#cart-drawer-footer');
  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (char) => ({'&':'&amp;','<':'&lt;','>':'&gt;',"'":'&#39;','"':'&quot;'}[char]));
  const money = (cents, currency = 'USD') => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
  const stripHtml = (html = '') => {
    const template = document.createElement('template');
    template.innerHTML = String(html || '');
    return (template.content.textContent || '').replace(/\s+/g, ' ').trim();
  };

  // Center-screen toast feedback for cart actions. One lightweight live region is
  // reused so repeated adds/removes never create a pile of DOM nodes.
  const toastRoot = qs('#invita-toast-root');
  let toastTimer = null;
  const showToast = (title, detail = '') => {
    if (!toastRoot) return;
    window.clearTimeout(toastTimer);
    toastRoot.innerHTML = `<div class="invita-toast" role="status"><span class="invita-toast-mark" aria-hidden="true">✓</span><span class="min-w-0"><strong>${escapeHtml(title)}</strong>${detail ? `<small>${escapeHtml(detail)}</small>` : ''}</span></div>`;
    const toast = toastRoot.firstElementChild;
    requestAnimationFrame(() => toast?.classList.add('is-visible'));
    toastTimer = window.setTimeout(() => {
      toast?.classList.remove('is-visible');
      window.setTimeout(() => { if (toastRoot.firstElementChild === toast) toastRoot.innerHTML = ''; }, 360);
    }, 2200);
  };

  const setCart = (open) => {
    if (!drawer || !overlay) return;
    drawer.classList.toggle('translate-x-full', !open);
    drawer.classList.toggle('translate-x-0', open);
    drawer.setAttribute('aria-hidden', String(!open));
    overlay.classList.toggle('opacity-0', !open);
    overlay.classList.toggle('opacity-100', open);
    overlay.classList.toggle('pointer-events-none', !open);
    overlay.classList.toggle('pointer-events-auto', open);
    lockBody(open);
  };

  const renderCart = async () => {
    if (!drawerContent || !drawerFooter) return;
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    qsa('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
    if (!cart.item_count) {
      drawerContent.innerHTML = '<div class="grid h-full place-items-center text-center"><div><p class="font-display text-4xl">Your bag is beautifully empty.</p><p class="mx-auto mt-3 max-w-xs text-sm leading-6 text-ink/55">Add an Invita scalp-care essential and continue the ritual at home.</p><a href="/collections/all" class="mt-6 inline-flex rounded-full bg-ink px-6 py-4 text-[10px] font-bold uppercase tracking-[0.18em] text-white">Explore the shop</a></div></div>';
      drawerFooter.innerHTML = '';
      return;
    }
    drawerContent.innerHTML = cart.items.map((item, index) => `
      <article class="flex gap-4 border-b border-ink/10 py-5 first:pt-0">
        <a href="${escapeHtml(item.url)}" class="h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-sand"><img src="${escapeHtml(item.image || '')}" alt="${escapeHtml(item.product_title)}" class="h-full w-full object-cover"></a>
        <div class="min-w-0 flex-1"><a href="${escapeHtml(item.url)}" class="font-display text-2xl leading-none">${escapeHtml(item.product_title)}</a><p class="mt-2 text-xs text-ink/45">Qty ${item.quantity}</p><div class="mt-3 flex items-center justify-between"><span class="text-sm font-semibold">${money(item.final_line_price, cart.currency || 'USD')}</span><button type="button" class="text-[9px] font-bold uppercase tracking-[0.15em] text-ink/45 underline" data-cart-remove="${index + 1}" data-cart-remove-title="${escapeHtml(item.product_title)}">Remove</button></div></div>
      </article>`).join('');
    drawerFooter.innerHTML = `<div class="flex items-end justify-between gap-4"><div><p class="text-[9px] font-bold uppercase tracking-[0.18em] text-ink/40">Subtotal</p><p class="mt-1 font-display text-3xl">${money(cart.total_price, cart.currency || 'USD')}</p></div><a href="/checkout" class="rounded-full bg-ink px-6 py-4 text-[10px] font-bold uppercase tracking-[0.18em] text-white transition hover:bg-bronze">Checkout</a></div><a href="/cart" class="mt-4 block text-center text-[9px] font-bold uppercase tracking-[0.16em] text-ink/50 underline">View full bag</a>`;
    qsa('[data-cart-remove]', drawerContent).forEach((button) => button.addEventListener('click', async () => {
      button.disabled = true;
      const removedTitle = button.dataset.cartRemoveTitle || 'Product';
      try {
        const response = await fetch('/cart/change.js', { method: 'POST', headers: {'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify({ line: Number(button.dataset.cartRemove), quantity: 0 }) });
        if (!response.ok) throw new Error('Remove failed');
        await renderCart();
        showToast('Removed from bag', removedTitle);
      } finally {
        button.disabled = false;
      }
    }));
  };

  qsa('[data-cart-open]').forEach((btn) => btn.addEventListener('click', async () => { setCart(true); await renderCart(); }));
  qsa('[data-cart-close]').forEach((btn) => btn.addEventListener('click', () => setCart(false)));

  qsa('form[action*="/cart/add"]').forEach((form) => form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submit = qs('[type="submit"]', form);
    if (submit) submit.disabled = true;
    try {
      const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      if (!response.ok) throw new Error('Add to cart failed');
      const item = await response.json();
      await renderCart();
      showToast('Added to bag', item.product_title || form.dataset.productTitle || 'Your selection');
    } catch (error) {
      form.submit();
    } finally {
      if (submit) submit.disabled = false;
    }
  }));

  // Full cart-page remove feedback. Shopify's cart line numbers are re-indexed after
  // a removal, so we refresh only after the centered confirmation has been seen.
  qsa('[data-cart-page-remove]').forEach((link) => link.addEventListener('click', async (event) => {
    event.preventDefault();
    const line = Number(link.dataset.line || 0);
    if (!line) return;
    link.setAttribute('aria-disabled', 'true');
    const row = link.closest('[data-cart-page-line]');
    try {
      const response = await fetch('/cart/change.js', { method: 'POST', headers: {'Content-Type':'application/json','Accept':'application/json'}, body: JSON.stringify({ line, quantity: 0 }) });
      if (!response.ok) throw new Error('Remove failed');
      const cart = await response.json();
      qsa('[data-cart-count]').forEach((el) => { el.textContent = cart.item_count; });
      const subtotal = qs('[data-cart-page-subtotal]');
      if (subtotal) subtotal.textContent = money(cart.total_price, cart.currency || window.Shopify?.currency?.active || 'USD');
      row?.classList.add('is-removing');
      showToast('Removed from bag', link.dataset.title || 'Product');
      window.setTimeout(() => window.location.reload(), 950);
    } catch (_) {
      window.location.href = link.href;
    }
  }));

  // Product quick view. Product JSON is fetched only when requested and cached for
  // subsequent opens, keeping initial storefront JavaScript/network cost minimal.
  const quickViewOverlay = qs('#invita-quick-view-overlay');
  const quickViewPanel = qs('#invita-quick-view-panel');
  const quickViewImage = qs('[data-quick-view-image]');
  const quickViewSkeleton = qs('[data-quick-view-skeleton]');
  const quickViewVendor = qs('[data-quick-view-vendor]');
  const quickViewTitle = qs('[data-quick-view-title]');
  const quickViewPrice = qs('[data-quick-view-price]');
  const quickViewDescription = qs('[data-quick-view-description]');
  const quickViewOptions = qs('[data-quick-view-options]');
  const quickViewVariant = qs('[data-quick-view-variant]');
  const quickViewAdd = qs('[data-quick-view-add]');
  const quickViewDetails = qs('[data-quick-view-details]');
  const productCache = new Map();
  let activeQuickProduct = null;

  const setQuickView = (open) => {
    if (!quickViewOverlay || !quickViewPanel) return;
    quickViewOverlay.classList.toggle('opacity-0', !open);
    quickViewOverlay.classList.toggle('opacity-100', open);
    quickViewOverlay.classList.toggle('pointer-events-none', !open);
    quickViewOverlay.classList.toggle('pointer-events-auto', open);
    quickViewPanel.classList.toggle('translate-y-5', !open);
    quickViewPanel.classList.toggle('translate-y-0', open);
    quickViewPanel.classList.toggle('scale-[.985]', !open);
    quickViewPanel.classList.toggle('scale-100', open);
    quickViewPanel.classList.toggle('opacity-0', !open);
    quickViewPanel.classList.toggle('opacity-100', open);
    quickViewOverlay.setAttribute('aria-hidden', String(!open));
    lockBody(open);
  };

  const productJsonUrl = (url) => `${String(url || '').replace(/\?.*$/, '').replace(/\/$/, '')}.js`;
  const fetchProduct = async (url) => {
    const key = productJsonUrl(url);
    if (productCache.has(key)) return productCache.get(key);
    const request = fetch(key, { headers: { Accept: 'application/json' } }).then((response) => {
      if (!response.ok) throw new Error('Product request failed');
      return response.json();
    });
    productCache.set(key, request);
    try { return await request; } catch (error) { productCache.delete(key); throw error; }
  };

  const selectedQuickVariant = () => {
    if (!activeQuickProduct) return null;
    const id = quickViewVariant?.value;
    return activeQuickProduct.variants?.find((variant) => String(variant.id) === String(id)) || activeQuickProduct.variants?.[0] || null;
  };

  const paintQuickVariant = () => {
    const variant = selectedQuickVariant();
    if (!variant) return;
    const currency = window.Shopify?.currency?.active || 'USD';
    if (quickViewPrice) quickViewPrice.textContent = money(variant.price, currency);
    if (quickViewAdd) {
      quickViewAdd.disabled = !variant.available;
      quickViewAdd.textContent = variant.available ? 'Add to bag' : 'Sold out';
    }
    const variantImage = variant.featured_image?.src || variant.featured_image || '';
    if (variantImage && quickViewImage) quickViewImage.src = variantImage;
  };

  const openQuickView = async (button) => {
    const url = button.dataset.productUrl;
    if (!url) return;
    setQuickView(true);
    activeQuickProduct = null;
    if (quickViewSkeleton) quickViewSkeleton.classList.remove('hidden');
    if (quickViewImage) { quickViewImage.classList.remove('opacity-100'); quickViewImage.classList.add('opacity-0'); quickViewImage.removeAttribute('src'); }
    if (quickViewTitle) quickViewTitle.textContent = 'Loading…';
    if (quickViewDescription) quickViewDescription.textContent = '';
    if (quickViewPrice) quickViewPrice.textContent = '';
    if (quickViewOptions) quickViewOptions.classList.add('hidden');
    if (quickViewAdd) quickViewAdd.disabled = true;
    try {
      const product = await fetchProduct(url);
      activeQuickProduct = product;
      if (quickViewVendor) quickViewVendor.textContent = product.vendor || 'Invita Head Spa';
      if (quickViewTitle) quickViewTitle.textContent = product.title || '';
      const description = stripHtml(product.description || '');
      if (quickViewDescription) quickViewDescription.textContent = description.length > 330 ? `${description.slice(0, 327).trim()}…` : description;
      if (quickViewDetails) quickViewDetails.href = product.url || url;
      const variants = Array.isArray(product.variants) ? product.variants : [];
      if (quickViewVariant) {
        quickViewVariant.innerHTML = variants.map((variant) => `<option value="${variant.id}" ${variant.available ? '' : 'disabled'}>${escapeHtml(variant.title)}${variant.available ? '' : ' · Sold out'}</option>`).join('');
        const firstAvailable = variants.find((variant) => variant.available) || variants[0];
        if (firstAvailable) quickViewVariant.value = String(firstAvailable.id);
      }
      if (quickViewOptions) quickViewOptions.classList.toggle('hidden', variants.length <= 1 && variants[0]?.title === 'Default Title');
      const rawImage = product.featured_image || product.images?.[0] || '';
      const image = typeof rawImage === 'string' ? rawImage : (rawImage?.src || '');
      if (quickViewImage && image) {
        quickViewImage.alt = product.title || '';
        quickViewImage.onload = () => { quickViewSkeleton?.classList.add('hidden'); quickViewImage.classList.remove('opacity-0'); quickViewImage.classList.add('opacity-100'); };
        quickViewImage.src = image;
      } else {
        quickViewSkeleton?.classList.add('hidden');
      }
      paintQuickVariant();
    } catch (_) {
      if (quickViewTitle) quickViewTitle.textContent = 'Unable to load product';
      if (quickViewDescription) quickViewDescription.textContent = 'Please open the full product page and try again.';
      if (quickViewDetails) quickViewDetails.href = url;
      quickViewSkeleton?.classList.add('hidden');
    }
  };

  qsa('[data-quick-view-open]').forEach((button) => {
    button.addEventListener('click', () => openQuickView(button));
    // Warm the cache only after real user intent, not during initial page load.
    button.addEventListener('pointerenter', () => fetchProduct(button.dataset.productUrl).catch(() => {}), { once: true, passive: true });
  });
  qsa('[data-quick-view-close]').forEach((button) => button.addEventListener('click', () => setQuickView(false)));
  quickViewVariant?.addEventListener('change', paintQuickVariant);
  quickViewAdd?.addEventListener('click', async () => {
    const variant = selectedQuickVariant();
    if (!variant?.available) return;
    quickViewAdd.disabled = true;
    try {
      const form = new FormData();
      form.append('id', variant.id);
      form.append('quantity', '1');
      const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: form });
      if (!response.ok) throw new Error('Quick add failed');
      const item = await response.json();
      await renderCart();
      setQuickView(false);
      showToast('Added to bag', item.product_title || activeQuickProduct?.title || 'Your selection');
    } catch (_) {
      quickViewAdd.disabled = false;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    setCart(false);
    setMobile(false);
    setSearch(false);
    setQuickView(false);
  });

  /* --------------------------------------------------------------
     INVITA IMMERSIVE MOTION + AMBIENT EXPERIENCE
     Reference-inspired behavior, implemented from scratch for Invita.
     -------------------------------------------------------------- */
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  // Scroll progress, selected image parallax and the dedicated ritual stack
  // share one requestAnimationFrame loop. Ordinary homepage cards are intentionally
  // NOT tied to scroll progress; they use stable entrance transitions instead.
  const parallaxContainers = qsa('[data-parallax-container]');
  const floatingStacks = qsa('[data-floating-stack]');
  const stackMotionStates = new WeakMap();
  let motionTicking = false;
  let lastMotionFrame = performance.now();

  const clamp01 = (value) => Math.max(0, Math.min(1, value));
  const smoothstep = (value) => {
    const t = clamp01(value);
    return t * t * (3 - 2 * t);
  };

  const paintScrollMotion = (now = performance.now()) => {
    const deltaMs = Math.max(8, Math.min(48, now - lastMotionFrame || 16.67));
    lastMotionFrame = now;
    let keepAnimating = false;
    if (!reduceMotion) {
      if (finePointer && window.innerWidth >= 1200) parallaxContainers.forEach((container) => {
        if (container.closest('[data-floating-stack]')) return;
        const rect = container.getBoundingClientRect();
        if (rect.bottom < -120 || rect.top > window.innerHeight + 120) return;
        const center = rect.top + rect.height / 2;
        const delta = (center - window.innerHeight / 2) / window.innerHeight;
        const y = Math.max(-10, Math.min(10, delta * -12));
        container.style.setProperty('--parallax-y', `${y.toFixed(2)}px`);
      });

      // Dedicated ritual story. Scroll sets a target; a time-based interpolation
      // glides toward that target so wheel/trackpad movement feels slow, continuous
      // and reversible instead of snapping between frames.
      floatingStacks.forEach((section) => {
        if (window.innerWidth < 1280) return;
        const rect = section.getBoundingClientRect();
        const viewport = window.innerHeight;
        // V2.0: the ritual no longer needs a tall sticky spacer. The deck unfolds
        // naturally while the section approaches the viewport, reaches its fully
        // readable state before the section top arrives, and stays open as the
        // section scrolls away. Scrolling upward reverses the same path.
        const revealStart = viewport * 0.88;
        const revealEnd = viewport * 0.24;
        const travel = Math.max(1, revealStart - revealEnd);
        const target = smoothstep((revealStart - rect.top) / travel);
        const stage = qs('[data-stack-stage]', section);
        const cards = qsa('[data-stack-card]', section);
        const bar = qs('[data-stack-progress-bar]', section);
        const label = qs('[data-stack-progress-label]', section);
        if (!stage || !cards.length) return;

        let state = stackMotionStates.get(section);
        if (!state) {
          state = { progress: target };
          stackMotionStates.set(section, state);
        }

        // About a quarter-second of visual follow-through. This is deliberately
        // slower than ordinary UI easing, but still catches the user's scroll.
        const alpha = 1 - Math.exp(-deltaMs / 320);
        const diff = target - state.progress;
        if (Math.abs(diff) > 0.00035) {
          state.progress += diff * alpha;
          keepAnimating = true;
        } else {
          state.progress = target;
        }

        const spread = clamp01(state.progress);
        const stageWidth = stage.clientWidth || 860;
        const stageHeight = stage.clientHeight || 744;
        const firstCard = cards[0];
        const cardWidth = firstCard.offsetWidth || 276;
        const cardHeight = firstCard.offsetHeight || 356;
        const maxX = Math.max(0, (stageWidth - cardWidth) / 2 - 18);
        const maxY = Math.max(0, (stageHeight - cardHeight) / 2 - 10);
        const xOffset = Math.min(maxX, Math.max(cardWidth * 0.66, stageWidth * 0.215));
        const yOffset = Math.min(maxY, cardHeight * 0.62 + 2);
        const count = cards.length;
        const center = (count - 1) / 2;
        const slots = [
          { sx: -1, sy: -1 },
          { sx:  1, sy: -1 },
          { sx: -1, sy:  1 },
          { sx:  1, sy:  1 }
        ];

        cards.forEach((card, index) => {
          const offset = index - center;
          const slot = slots[index] || { sx: index % 2 ? 1 : -1, sy: Math.floor(index / 2) % 2 ? 1 : -1 };
          const stagger = index * 0.012;
          const local = smoothstep((spread - stagger) / Math.max(0.001, 1 - stagger));
          const closedX = offset * 5.5;
          const closedY = Math.abs(offset) * 2.5;
          const closedRotation = offset * 0.72;
          const x = closedX + (slot.sx * xOffset - closedX) * local;
          const y = closedY + (slot.sy * yOffset - closedY) * local;
          const rotation = closedRotation * (1 - local);
          const scale = 0.955 + local * 0.045;
          const opacity = 0.80 + local * 0.20;

          card.style.zIndex = String(40 + index);
          card.style.opacity = opacity.toFixed(3);
          card.style.transform = `translate3d(calc(-50% + ${x.toFixed(2)}px), calc(-50% + ${y.toFixed(2)}px), 0) rotate(${rotation.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
          card.style.setProperty('--stack-open', local.toFixed(4));
        });

        if (bar) bar.style.transform = `scaleX(${spread.toFixed(4)})`;
        if (label) {
          const labelIndex = Math.min(cards.length - 1, Math.max(0, Math.floor(spread * cards.length)));
          const title = qs('h3', cards[labelIndex])?.textContent?.trim() || 'Ritual';
          label.textContent = `${String(labelIndex + 1).padStart(2, '0')} · ${title}`;
        }
      });
    }

    if (keepAnimating) {
      motionTicking = true;
      requestAnimationFrame(paintScrollMotion);
    } else {
      motionTicking = false;
    }
  };

  const requestMotionPaint = () => {
    if (motionTicking) return;
    motionTicking = true;
    lastMotionFrame = performance.now();
    requestAnimationFrame(paintScrollMotion);
  };
  requestMotionPaint();
  window.addEventListener('scroll', requestMotionPaint, { passive: true });
  window.addEventListener('resize', requestMotionPaint, { passive: true });

  // Keep the homepage hero inside the first desktop/laptop viewport. The
  // measured offset automatically includes the announcement bar + site header.
  const homeHero = qs('.invita-home-hero');
  const syncHomeHeroViewport = () => {
    if (!homeHero || window.innerWidth < 1024) {
      document.documentElement.style.removeProperty('--invita-home-hero-offset');
      return;
    }
    const heroTop = Math.max(0, homeHero.getBoundingClientRect().top + window.scrollY);
    document.documentElement.style.setProperty('--invita-home-hero-offset', `${Math.round(heroTop)}px`);
  };
  syncHomeHeroViewport();
  window.addEventListener('load', syncHomeHeroViewport, { once: true });
  window.addEventListener('resize', syncHomeHeroViewport, { passive: true });

  // Editorial word-by-word reveals for hero and section headings.
  const splitHeading = (heading) => {
    if (reduceMotion || heading.classList.contains('invita-split-ready') || heading.children.length) return;
    const text = heading.textContent.trim();
    if (!text) return;
    heading.setAttribute('aria-label', text);
    const words = text.split(/\s+/);
    heading.innerHTML = words.map((word, index) => `<span class="invita-word-wrap" aria-hidden="true"><span class="invita-word" style="--word-index:${index}">${escapeHtml(word)}</span></span>${index < words.length - 1 ? ' ' : ''}`).join('');
    heading.classList.add('invita-split-ready');
  };
  const splitTargets = qsa('[data-split-text]');
  splitTargets.forEach(splitHeading);
  if (!reduceMotion && splitTargets.length) {
    const wordObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-word-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.25, rootMargin: '0px 0px -6%' });
    splitTargets.forEach((heading) => wordObserver.observe(heading));
  } else {
    splitTargets.forEach((heading) => heading.classList.add('is-word-visible'));
  }

  // Image entrances stay visible even before activation; the motion is enhancement,
  // never a gate that can leave a section blank.
  const imageReveals = qsa('[data-image-reveal]');
  if (!reduceMotion && imageReveals.length) {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-image-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.04, rootMargin: '16% 0px 8%' });
    imageReveals.forEach((el) => imageObserver.observe(el));
    // Defensive fallback for browser/theme-editor edge cases.
    window.setTimeout(() => imageReveals.forEach((el) => el.classList.add('is-image-visible')), 1400);
  } else {
    imageReveals.forEach((el) => el.classList.add('is-image-visible'));
  }

  // Media reliability: never time out lazy images. A previous watchdog replaced every
  // below-the-fold photo before the browser had a chance to lazy-load it. We now
  // fall back ONLY after a genuine network/decode error.
  qsa('img[data-invita-image]').forEach((img) => {
    const fallback = img.dataset.fallbackSrc;
    if (!fallback) return;

    const useFallback = () => {
      if (img.dataset.fallbackApplied === '1') return;
      img.dataset.fallbackApplied = '1';
      img.removeAttribute('srcset');
      img.src = fallback;
      img.classList.add('invita-local-image-fallback');
    };

    img.addEventListener('error', useFallback, { once: true });

    // If an image was already attempted before this script initialized and truly
    // failed, catch that state. `complete === false` is NOT an error for lazy media.
    if (img.complete && img.naturalWidth === 0) useFallback();
  });

  // Stagger cards without tying animation progress to scroll position.
  qsa('[data-stagger-group]').forEach((group) => {
    qsa('[data-reveal]', group).forEach((child, index) => {
      child.style.transitionDelay = `${Math.min(index * 65, 260)}ms`;
    });
  });

  // Highlight the ritual currently nearest the reading zone.
  const ritualSteps = qsa('[data-ritual-step]');
  if (ritualSteps.length) {
    const ritualObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => entry.target.classList.toggle('is-ritual-active', entry.isIntersecting));
    }, { threshold: 0.55, rootMargin: '-12% 0px -24%' });
    ritualSteps.forEach((step) => ritualObserver.observe(step));
  }

  // Premium gallery lightbox. Images stay lazy in the grid and the large viewer
  // is populated only after the visitor opens a photo, keeping the gallery fast.
  qsa('[data-gallery]').forEach((gallery) => {
    const modal = qs('[data-gallery-modal]', gallery);
    const modalImage = qs('[data-gallery-modal-image]', gallery);
    const modalCaption = qs('[data-gallery-modal-caption]', gallery);
    const modalLocation = qs('[data-gallery-modal-location]', gallery);
    const counter = qs('[data-gallery-counter]', gallery);
    const items = qsa('[data-gallery-open]', gallery);
    if (!modal || !modalImage || !items.length) return;

    let activeIndex = 0;
    let previousFocus = null;

    const paintGalleryItem = (index) => {
      activeIndex = (index + items.length) % items.length;
      const item = items[activeIndex];
      modalImage.src = item.dataset.gallerySrc || '';
      modalImage.alt = item.dataset.galleryAlt || '';
      if (modalCaption) modalCaption.textContent = item.dataset.galleryCaption || '';
      if (modalLocation) modalLocation.textContent = item.dataset.galleryLocation || '';
      if (counter) counter.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(items.length).padStart(2, '0')}`;
    };

    const openGallery = (index) => {
      previousFocus = document.activeElement;
      paintGalleryItem(index);
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      lockBody(true);
      window.setTimeout(() => qs('[data-gallery-close]', modal)?.focus(), 40);
    };

    const closeGallery = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      lockBody(false);
      window.setTimeout(() => previousFocus?.focus?.(), 40);
    };

    items.forEach((item, index) => item.addEventListener('click', () => openGallery(index)));
    qs('[data-gallery-close]', modal)?.addEventListener('click', closeGallery);
    qs('[data-gallery-prev]', modal)?.addEventListener('click', () => paintGalleryItem(activeIndex - 1));
    qs('[data-gallery-next]', modal)?.addEventListener('click', () => paintGalleryItem(activeIndex + 1));
    modal.addEventListener('click', (event) => { if (event.target === modal) closeGallery(); });

    document.addEventListener('keydown', (event) => {
      if (!modal.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeGallery();
      if (event.key === 'ArrowLeft') paintGalleryItem(activeIndex - 1);
      if (event.key === 'ArrowRight') paintGalleryItem(activeIndex + 1);
    });
  });

  // Cinematic internal-page transition; disabled in Shopify Theme Editor.
  if (!reduceMotion && !(window.Shopify && window.Shopify.designMode)) {
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank' || link.hasAttribute('download') || link.dataset.noTransition !== undefined) return;
      let url;
      try { url = new URL(link.href, window.location.href); } catch (_) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
      event.preventDefault();
      document.documentElement.classList.add('invita-page-leaving');
      window.setTimeout(() => { window.location.href = url.href; }, 520);
    });
    window.addEventListener('pageshow', () => document.documentElement.classList.remove('invita-page-leaving'));
  }

  // Ambient spa sound: file-based when a Shopify Files URL is configured,
  // otherwise a lightweight generative soundscape is created with Web Audio.
  // Browsers may block audible autoplay until the first click/tap/key press.
  const ambientController = qs('#invita-ambient-controller');
  const ambientAudio = qs('#invita-ambient-audio');
  const audioToggle = qs('#invita-audio-toggle');
  if (ambientController) {
    const ambientPreferenceKey = 'invita-ambient-choice';
    const readAmbientPreference = () => {
      try {
        const value = localStorage.getItem(ambientPreferenceKey);
        if (value === 'on' || value === 'off') return value;
      } catch (_) {}
      const cookieMatch = document.cookie.match(/(?:^|;\s*)invita_ambient_choice=(on|off)(?:;|$)/);
      return cookieMatch ? cookieMatch[1] : null;
    };
    const saveAmbientPreference = (value) => {
      try { localStorage.setItem(ambientPreferenceKey, value); } catch (_) {}
      document.cookie = `invita_ambient_choice=${value}; Max-Age=31536000; Path=/; SameSite=Lax`;
    };
    const storedAudioChoice = readAmbientPreference();
    let wantsAudio = storedAudioChoice !== 'off';
    const desiredVolume = Math.max(0.025, Math.min(0.16, Number(audioToggle?.dataset.volume || 0.11) * 0.72));
    const mode = ambientController.dataset.ambientMode || (ambientAudio ? 'file' : 'synth');
    let synth = null;
    let suspendTimer = null;
    let fileFadeFrame = null;

    const fadeFileAudio = (target, duration = 1200) => {
      if (!ambientAudio) return;
      if (fileFadeFrame) cancelAnimationFrame(fileFadeFrame);
      const from = ambientAudio.volume;
      const startedAt = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        ambientAudio.volume = from + (target - from) * eased;
        if (progress < 1) fileFadeFrame = requestAnimationFrame(tick);
        else fileFadeFrame = null;
      };
      fileFadeFrame = requestAnimationFrame(tick);
    };

    if (ambientAudio) {
      // Never let the HTML element race ahead of the saved preference on reload.
      // Playback is started only by startAmbient() after the persisted choice is known.
      ambientAudio.autoplay = false;
      ambientAudio.volume = 0;
      ambientAudio.muted = !wantsAudio;
      if (!wantsAudio) ambientAudio.pause();
      ambientAudio.addEventListener('playing', () => syncAudioUI('playing'));
    }

    const syncAudioUI = (state) => {
      if (!audioToggle) return;
      const label = qs('[data-audio-label]', audioToggle);
      const onIcon = qs('[data-audio-icon-on]', audioToggle);
      const offIcon = qs('[data-audio-icon-off]', audioToggle);
      audioToggle.classList.toggle('is-muted', !wantsAudio || state === 'muted');
      audioToggle.classList.toggle('is-pending', wantsAudio && state === 'pending');
      audioToggle.setAttribute('aria-pressed', String(wantsAudio));
      audioToggle.setAttribute('aria-label', wantsAudio ? 'Turn relaxing sound off' : 'Turn relaxing sound on');
      if (label) label.textContent = state === 'pending' ? 'Tap for sound' : (wantsAudio ? (audioToggle.dataset.labelOn || 'Sound on') : (audioToggle.dataset.labelOff || 'Sound off'));
      if (onIcon) onIcon.classList.toggle('hidden', !wantsAudio);
      if (offIcon) offIcon.classList.toggle('hidden', wantsAudio);
    };

    const ensureSynth = () => {
      if (synth) return synth;
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) return null;
      const context = new AudioContextClass();
      const master = context.createGain();
      const compressor = context.createDynamicsCompressor();
      compressor.threshold.value = -28;
      compressor.knee.value = 18;
      compressor.ratio.value = 2;
      compressor.attack.value = 0.12;
      compressor.release.value = 1.2;
      master.gain.value = 0;
      master.connect(compressor);
      compressor.connect(context.destination);

      const padFilter = context.createBiquadFilter();
      padFilter.type = 'lowpass';
      padFilter.frequency.value = 1050;
      padFilter.Q.value = 0.25;
      padFilter.connect(master);

      const frequencies = [110, 130.8128, 164.8138, 196, 246.9417, 329.6276];
      frequencies.forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        const voiceGain = context.createGain();
        oscillator.type = index < 2 ? 'sine' : 'triangle';
        oscillator.frequency.value = frequency;
        voiceGain.gain.value = index < 3 ? 0.035 : 0.012;

        const lfo = context.createOscillator();
        const lfoDepth = context.createGain();
        lfo.type = 'sine';
        lfo.frequency.value = 0.025 + index * 0.006;
        lfoDepth.gain.value = index < 3 ? 0.009 : 0.004;
        lfo.connect(lfoDepth);
        lfoDepth.connect(voiceGain.gain);

        if (context.createStereoPanner) {
          const pan = context.createStereoPanner();
          pan.pan.value = ((index % 3) - 1) * 0.28;
          oscillator.connect(voiceGain);
          voiceGain.connect(pan);
          pan.connect(padFilter);
        } else {
          oscillator.connect(voiceGain);
          voiceGain.connect(padFilter);
        }
        oscillator.start();
        lfo.start();
      });

      // Gentle filtered air gives the soundscape a spa-like water/steam texture.
      const noiseBuffer = context.createBuffer(1, context.sampleRate * 3, context.sampleRate);
      const noiseData = noiseBuffer.getChannelData(0);
      for (let i = 0; i < noiseData.length; i += 1) noiseData[i] = (Math.random() * 2 - 1) * 0.22;
      const noise = context.createBufferSource();
      const noiseFilter = context.createBiquadFilter();
      const noiseGain = context.createGain();
      noise.buffer = noiseBuffer;
      noise.loop = true;
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = 720;
      noiseFilter.Q.value = 0.45;
      noiseGain.gain.value = 0.018;
      noise.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(master);
      noise.start();

      synth = { context, master };
      return synth;
    };

    const fadeSynth = (target) => {
      if (!synth) return;
      const now = synth.context.currentTime;
      synth.master.gain.cancelScheduledValues(now);
      synth.master.gain.setValueAtTime(synth.master.gain.value, now);
      synth.master.gain.linearRampToValueAtTime(target, now + 0.8);
    };

    const startAmbient = async () => {
      if (!wantsAudio) return false;
      try {
        if (mode === 'file' && ambientAudio) {
          ambientAudio.muted = false;
          ambientAudio.volume = 0;
          await ambientAudio.play();
          fadeFileAudio(desiredVolume, 1600);
        } else {
          const engine = ensureSynth();
          if (!engine) throw new Error('Web Audio unavailable');
          if (suspendTimer) { clearTimeout(suspendTimer); suspendTimer = null; }
          await engine.context.resume();
          if (engine.context.state !== 'running') throw new Error('Autoplay blocked');
          fadeSynth(desiredVolume * 0.78);
        }
        syncAudioUI('playing');
        return true;
      } catch (_) {
        syncAudioUI('pending');
        return false;
      }
    };

    const stopAmbient = () => {
      if (ambientAudio) {
        if (fileFadeFrame) { cancelAnimationFrame(fileFadeFrame); fileFadeFrame = null; }
        ambientAudio.pause();
        ambientAudio.volume = 0;
        ambientAudio.muted = true;
      }
      if (synth) {
        fadeSynth(0);
        suspendTimer = window.setTimeout(() => synth?.context.suspend().catch(() => {}), 900);
      }
      syncAudioUI('muted');
    };

    if (wantsAudio) startAmbient();
    else stopAmbient();

    audioToggle?.addEventListener('click', async (event) => {
      event.stopPropagation();
      wantsAudio = !wantsAudio;
      saveAmbientPreference(wantsAudio ? 'on' : 'off');
      if (wantsAudio) await startAmbient();
      else stopAmbient();
    });

    const unlockAudio = async (event) => {
      if (!wantsAudio || event.target.closest('#invita-audio-toggle')) return;
      const started = await startAmbient();
      if (started) {
        document.removeEventListener('pointerdown', unlockAudio, true);
        document.removeEventListener('keydown', unlockAudio, true);
      }
    };
    document.addEventListener('pointerdown', unlockAudio, true);
    document.addEventListener('keydown', unlockAudio, true);
    window.addEventListener('pageshow', () => {
      const preference = readAmbientPreference();
      if (preference === 'off') {
        wantsAudio = false;
        stopAmbient();
      }
    });
  }
})();
